//* 按需导入插件
import { gsap } from "gsap";
import type { SetupContext } from "vue";
//* 组件库
import type { UiTableOption2 } from "various-ui";
//* 组件属性
import type { SearchDiagramVars, SearchDiagramProps, SearchDiagramEmits } from "./index";
//* 样式（module）
import css from "../style/index.module.less";

export const useComposable = (define: SearchDiagramProps, emits: SetupContext<typeof SearchDiagramEmits>["emit"]) => {
    //* 响应式变量
    const refs = {
        sortKey: ref<string>(""),
        sortValue: ref<string>(""),
    };

    //* 静态变量
    const variable = {
        size: shallowRef<number>(),
        observer: shallowRef<ResizeObserver>(),
    };

    //* 节点
    const nodes = {
        main: ref<HTMLDivElement>(),
    };

    //* 函数列表
    const methods = {
        //* 初始化
        init: () => {
            //* 检索表头对象与表格内容对象
            const header = nodes.main.value?.querySelector(`.${css.header}`) as HTMLElement;
            const bodys = nodes.main.value?.querySelector(`.${css.bodys}`) as HTMLElement;
            //* 检测是否存在表头对象与表格内容对象
            if (!header || !bodys) return;

            //* 数据初始化
            const body = bodys.querySelectorAll(`.${css.body}`) as NodeListOf<HTMLElement>;
            const rows = [header, ...body];
            const vars = {
                replenish: 0,
                size: Math.floor(header.clientWidth) - 4, //* 这里-4是预留宽度，用于解决因小数点计算导致的尺寸偏差
                data: <any[]>[],
            };

            //* 容器宽度不足, 停止初始化
            if (vars.size <= 0) return;

            //* 第一次遍历进行数据初始化
            define.option.forEach((value) => {
                //* 数据初始化
                const result = {
                    key: value.key,
                    min: value["min-width"] || 0,
                    max: value["max-width"] || 0,
                    width: value.width || value["min-width"] || 0,
                    replenish: !value.width,
                };

                //* 遍历td、th用于获取row尺寸
                if (!value.width) {
                    rows.forEach((row) => {
                        //* 获取对应的col node
                        const node = row.querySelector(`.${css.column}[d-name=${value.key}]`) as HTMLElement;
                        const context = node?.firstElementChild as HTMLElement;
                        //* 检测col node是否存在
                        if (!node) return;
                        else {
                            //* 重置node属性
                            node.style.whiteSpace = "nowrap";
                            node.style.width = "";
                            if (context) {
                                context.style.width = "";
                            }

                            //* 判断当前尺寸是否为最大尺寸
                            if (node.clientWidth > result.width) {
                                if (result.max && node.clientWidth > result.max) {
                                    result.width = result.max;
                                } else {
                                    result.width = Math.ceil(node.clientWidth) + 4;
                                }
                            }

                            //* 回退样式调整
                            node.style.whiteSpace = "";
                        }
                    });
                }

                //* 数据添加
                vars.data.push(result);
            });

            //* 统计实际表格所需尺寸
            const real = vars.data.reduce((former, current) => {
                return former + current.width;
            }, 0);

            //* 第二次遍历, 检测当前表格是需要进行补足还是删减尺寸, 并进行对应操作
            vars.replenish = vars.size - real;
            while (Math.floor(Math.abs(vars.replenish)) != 0) {
                //* 初始化允许增减长度的数据列表
                const data = vars.data.filter((val) => {
                    return val.replenish && (vars.replenish > 0 ? !val.max || val.width < val.max : !val.min || val.width > val.min);
                });

                //* 当不存在允许增减长度的数据列表时退出...
                if (!data.length || vars.replenish / data.length == 0) break;

                //* 增减尺寸...
                if (vars.replenish > 0) {
                    //* 补充尺寸（平均分配）
                    const replenish = vars.replenish / data.length;
                    data.forEach((value) => {
                        if (value.max && value.width + replenish > value.max) {
                            vars.replenish -= value.max - value.width;
                            value.width = value.max;
                        } else {
                            value.width += replenish;
                            vars.replenish -= replenish;
                        }
                    });
                } else if (vars.replenish < 0) {
                    //* 删减尺寸（优先删减最长的尺寸）
                    //* 获取data中最大的尺寸
                    const max = data.sort((a, b) => b.width - a.width)?.[0]?.width || 0;
                    for (let i = 0; i < data.length; i++) {
                        if (vars.replenish == 0) break;
                        if (data[i].width == max && data[i].width > data[i].min) {
                            //* 当width与min之间的距离小于1时：特殊处理
                            if (data[i].min && data[i].width - 1 < data[i].min) {
                                vars.replenish += data[i].width - data[i].min;
                                data[i].width = data[i].min;
                            } else {
                                vars.replenish += 1;
                                data[i].width -= 1;
                            }
                        }
                    }
                }
            }

            //* 第三次遍历设置定框的Row
            vars.data.forEach((value) => {
                //* 遍历td、th用于获取row尺寸
                rows.forEach((row) => {
                    //* 获取对应的col node
                    const node = row.querySelector(`.${css.column}[d-name=${value.key}]`) as HTMLElement;
                    //* 检测col node是否存在
                    if (!node) return;
                    else {
                        //* 设置尺寸
                        node.style.width = value.width + "px";
                    }
                });
            });

            //* 第四次遍历设置特殊对齐模式
            define.option
                .filter((value) => value.align == "singularity")
                .forEach((value) => {
                    //* 数据初始化
                    let result = 0;
                    //* 统计列宽度的最大值
                    body.forEach((row) => {
                        const node = row.querySelector(`.${css.column}[d-name=${value.key}] > div`) as HTMLElement;
                        if (!node) return;
                        else {
                            result = Math.max(result, node.offsetWidth);
                        }
                    });

                    //* 设置列宽
                    body.forEach((row) => {
                        const node = row.querySelector(`.${css.column}[d-name=${value.key}] > div`) as HTMLElement;
                        if (!node || node?.offsetWidth >= result) return;
                        else {
                            node.style.width = result + "px";
                        }
                    });
                });
        },

        //* 清空状态
        clear: () => {
            refs.sortKey.value = "";
            refs.sortValue.value = "";
        },

        //* 排序切换
        switchSort: (row: UiTableOption2) => {
            //* 切换已排序字段时的处理, 其余都为切换未排序字段
            if (refs.sortKey.value == row.key) {
                //* 排序为升序时, 切换为降序, 其余都为取消排序
                if (refs.sortValue.value == "asc") {
                    refs.sortValue.value = "desc";
                } else {
                    refs.sortKey.value = "";
                    refs.sortValue.value = "";
                }
            } else {
                refs.sortKey.value = row.key;
                refs.sortValue.value = "asc";
            }

            //* 响应排序事件
            emits("sort", refs.sortKey.value, refs.sortValue.value);
        },
    };

    //* 动画函数
    const animation = {
        //* 离场动画
        aniLeave: (index: number) => {
            const bodys = nodes.main.value?.querySelectorAll(`.${css.body}`) || [];
            if (bodys[index]) {
                bodys[index].classList.remove("active");
                gsap.set(bodys[index], { zIndex: 1 });
                gsap.to(bodys[index], {
                    duration: 0.5,
                    boxShadow: "0px 8px 20px 0px transparent",
                    padding: 0,
                    margin: 0,
                    onComplete: () => {
                        gsap.set(bodys[index], { zIndex: "auto" });
                    },
                });
            }
        },

        //* 入场动画
        aniEnter: (index: number) => {
            const bodys = nodes.main.value?.querySelectorAll(`.${css.body}`) || [];
            if (bodys[index]) {
                bodys[index].classList.add("active");
                gsap.set(bodys[index], { zIndex: 2 });
                gsap.to(bodys[index], {
                    duration: 0.5,
                    boxShadow: define.hover?.["box-shadow"] || "0px 8px 20px 0px rgba(114, 41, 157, 0.08)",
                    padding: `0 ${define.hover?.spacing || 40}px`,
                    margin: `0 -${define.hover?.spacing || 40}px`,
                    onComplete: () => {
                        gsap.set(bodys[index], { zIndex: 1 });
                    },
                });
            }
        },
    };

    return { refs, nodes, methods, variable, animation };
};
